import React, {useCallback, useState} from 'react';
import classnames from 'classnames';
import {useDropzone} from 'react-dropzone';
import {useMutation} from 'react-apollo-hooks';
import gql from 'graphql-tag';
import {Card} from 'ui-kit';
import {FormFooter} from '../../../FormFooter';
import {DropzoneContent} from './components';

import * as styles from './LogoUpload.module.css';

export function LogoUpload({logoUrl, onUpload}) {
  const [file, setFile] = useState();
  const [fileData, setFileData] = useState();
  const [status, setStatus] = useState();
  const [errors, setErrors] = useState([]);

  const mutate = useMutation(gql`
    mutation Logo($file: Upload!) {
      attachPracticeLogo(logo: $file) {
        errors {
          path
        }
      }
    }
  `);

  const onDropAccepted = useCallback(acceptedFiles => {
    const [firstFile] = acceptedFiles;

    const dataReader = new FileReader();
    dataReader.onabort = () => console.log('file reading was aborted');
    dataReader.onerror = () => console.log('file reading has failed');
    dataReader.onload = () => {
      setFile(firstFile);
      setFileData(dataReader.result);
    };
    dataReader.readAsDataURL(firstFile);
  }, []);

  const onDropRejected = useCallback(rejectedFiles => {
    setFile(null);
    setFileData(null);

    const rejectReasons = [];

    if (rejectedFiles.length > 1) {
      rejectReasons.push('You can only upload one file');
      setErrors(rejectReasons);
      return;
    }

    const [file] = rejectedFiles;

    if (!file.type.startsWith('image')) {
      rejectReasons.push('File must be an image');
    }

    if (file.size > 1e7) {
      rejectReasons.push(
        'File size is too big; must be less than 10 megabytes',
      );
    }

    setErrors(rejectReasons);
  }, []);

  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    onDropAccepted,
    onDropRejected,
    accept: 'image/*',
    maxSize: 1e7, // 10 megabytes
    multiple: false,
  });

  async function handleSubmit(event) {
    event.preventDefault();
    setStatus('loading');
    setErrors([]);

    try {
      await mutate({variables: {file}});

      await onUpload();

      setFile(null);
      setFileData(null);

      setStatus(null);
    } catch (error) {
      setErrors(['An unexpected error occurred. Please try again later.']);
      setStatus(null);
      setFile(null);
      setFileData(null);
    }
  }

  const dropzoneClassNames = classnames(styles.Dropzone, {
    [styles['Dropzone-active']]: isDragActive,
    [styles['Dropzone-error']]: errors.length > 0,
  });

  return (
    <div className={styles.Container}>
      <Card>
        <p>
          <strong>Practice logo</strong>
        </p>
        <p>
          Your practice logo will be displayed to your patients on your plan
          signup page.
        </p>

        <form method="post" onSubmit={handleSubmit}>
          <div {...getRootProps({className: dropzoneClassNames})}>
            <input {...getInputProps()} />
            <div>
              <DropzoneContent wipImage={fileData} savedImage={logoUrl} />
            </div>
          </div>

          {errors.length === 0 ? null : (
            <>
              <ul className={styles.ErrorList}>
                {errors.map(error => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </>
          )}

          <FormFooter status={status} />
        </form>
      </Card>
    </div>
  );
}
